import React, { useState } from 'react';
import {
  Box,
  TextField,
  Popover,
  ClickAwayListener,
} from '@mui/material';
import { DayPicker, DateRange } from 'react-day-picker';
import { ja } from 'date-fns/locale';
import { startOfWeek, addDays, format, parseISO, isValid } from 'date-fns';
import 'react-day-picker/dist/style.css';
import './style.css';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

type Props = {
  start: string;
  end:   string;
  onChange: (r: { from: string; to: string }) => void;
  enlightSyncEnabled?: boolean;
};

export const WeeklyDatePicker: React.FC<Props> = ({ start, end, onChange, enlightSyncEnabled }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const parsedStartDate = parseISO(start);
  const parsedEndDate = parseISO(end);

  const initialRange =
    isValid(parsedStartDate) && isValid(parsedEndDate)
      ? { from: parsedStartDate, to: parsedEndDate }
      : undefined;

  const [range, setRange] = useState<DateRange | undefined>(initialRange);

  /* ---------- 同期アイコン ---------- */
  const SyncIcon = enlightSyncEnabled ? (
    <CheckCircleIcon sx={{ fontSize: 16, verticalAlign: 'middle' }} color="success" />
  ) : (
    <CheckCircleOutlineIcon sx={{ fontSize: 16, verticalAlign: 'middle' }} color="disabled" />
  );

  const makeWeek = (d: Date): DateRange => {
    const sun = startOfWeek(d, { weekStartsOn: 1, locale: ja });
    // (月,火,水,木,金 の5日間)
    return { from: sun, to: addDays(sun, 4) };
  };

  const handleSelect = (
    _selectedRangeByPicker: DateRange | undefined,
    clickedDay: Date
  ) => {
    if (!isValid(clickedDay)) {
      console.warn('handleSelect received an invalid clickedDay:', clickedDay);
      return;
    }

    const w = makeWeek(clickedDay);

    if (!w.from || !w.to || !isValid(w.from) || !isValid(w.to)) {
        console.error("makeWeek generated an invalid date range:", w, "from clickedDay:", clickedDay);
        return;
    }

    setRange(w);

    onChange({
      from: format(w.from, 'yyyy/MM/dd'),
      to:   format(w.to, 'yyyy/MM/dd'),
    });
  };

  const jpFmt = {
    formatCaption: (captionDate: Date) => {
      if (!isValid(captionDate)) return "無効な年月";
      return `${captionDate.getFullYear()}年${captionDate.getMonth() + 1}月`;
    },
    formatWeekdayName: (weekdayDate: Date) => {
      if (!isValid(weekdayDate)) return "";
      return format(weekdayDate, 'E', { locale: ja });
    },
  };

  const openPicker  = (e: React.MouseEvent<HTMLInputElement>) => setAnchorEl(e.currentTarget);
  const closePicker = () => setAnchorEl(null);

  let defaultMonthDate: Date;
  if (range?.from && isValid(range.from)) {
    defaultMonthDate = range.from;
  } else if (isValid(parsedStartDate)) {
    defaultMonthDate = parsedStartDate;
  } else {
    defaultMonthDate = new Date();
  }

  return (
    <ClickAwayListener onClickAway={closePicker}>
      <Box className="weekly-date-fields" sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
        <div className="startDate">
          <div className='start-date-icon'>
            <label htmlFor="startDate" className="dateLabel" id='required-label'>開始日付</label>
            <div>&nbsp;{SyncIcon}</div>
          </div>
          <TextField
            value={start}
            onClick={openPicker}
            placeholder="年/月/日"
            id="startDate"
          />
        </div>

        <div className="endDate">
          <label htmlFor="endDate" className="dateLabel">終了日付&nbsp;{SyncIcon}</label>
          <TextField
            value={end}
            onClick={openPicker}
            placeholder="年/月/日"
            id="endDate"
          />
        </div>

        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={closePicker}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <DayPicker
            locale={ja}
            showOutsideDays
            defaultMonth={defaultMonthDate}
            mode="range"
            selected={range}
            onSelect={handleSelect}
            formatters={jpFmt}
            disabled={{ dayOfWeek: [0, 6] }}
            modifiersClassNames={{
              selected: 'my-selected',
            }}
            className="weekly-range-picker"
            weekStartsOn={0} // 週の開始を日曜日
          />
        </Popover>
      </Box>
    </ClickAwayListener>
  );
};
